import { useEffect, useState } from "react";

import { useAuthStore } from "@/features/auth/store";
import { useGroupRooms } from "../api/groupChat";
import { GroupRoomsPanel } from "./GroupRoomsPanel";
import { GroupChatThread } from "./GroupChatThread";
import { ChatThreadSkeleton, ConversationsPanelSkeleton } from "./ChatSkeletons";

/** Group-chat content: room list · active room thread. Mirrors the content block of
 *  DashboardLayout (everything to the right of the IconRail), so the integration step can
 *  render it inside the same shell — `<div …gradient><IconRail/><GroupRoomsView/></div>` —
 *  or behind its own route. It does NOT render the IconRail or the background wrapper.
 *
 *  Responsive: at md+ the room list and the thread sit side by side; on smaller screens
 *  only one shows at a time (list, or thread with a back button). */
export function GroupRoomsView() {
  const myId = useAuthStore((s) => s.user?.id ?? null);
  const { data: rooms, isLoading, isError } = useGroupRooms();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Mobile-only: which pane is visible. Ignored at md+ (both always show).
  const [mobileView, setMobileView] = useState<"list" | "thread">("list");

  const roomList = rooms ?? [];

  // Select the first room once data loads (for the md+ side-by-side view).
  useEffect(() => {
    if (selectedId == null && roomList.length) setSelectedId(roomList[0].loanId);
  }, [roomList, selectedId]);

  const selected = roomList.find((r) => r.loanId === selectedId) ?? null;

  const openRoom = (loanId: number) => {
    setSelectedId(loanId);
    setMobileView("thread");
  };

  if (isLoading) {
    return (
      <>
        <ConversationsPanelSkeleton className="flex" />
        <ChatThreadSkeleton className="hidden md:flex" />
      </>
    );
  }
  if (isError) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-red-300">
        Kunne ikke laste grupperom.
      </div>
    );
  }

  return (
    <>
      <GroupRoomsPanel
        rooms={roomList}
        selectedId={selectedId}
        onSelect={openRoom}
        className={mobileView === "thread" ? "hidden md:flex" : "flex"}
      />
      {selected ? (
        <GroupChatThread
          key={selected.loanId}
          room={selected}
          myId={myId}
          onBack={() => setMobileView("list")}
          className={mobileView === "list" ? "hidden md:flex" : "flex"}
        />
      ) : (
        <div className="hidden flex-1 items-center justify-center text-sm text-white/40 md:flex">
          {roomList.length
            ? "Velg et grupperom for å komme i gang."
            : "Du deltar ikke i noen grupperom ennå. Skru på gruppe-chat i Innstillinger."}
        </div>
      )}
    </>
  );
}
